import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  User,
  Wallet,
  FileText,
  LogOut,
  Coins,
  Plus,
  Eye,
  Clock,
  CheckCircle,
  Camera,
  Mail,
  Phone,
  CreditCard,
  TrendingUp,
  Gift,
  Edit3,
  Trash2,
  ChevronRight,
  Megaphone,
  Calendar,
  X,
  MapPin,
  Briefcase,
  Save,
  Loader2,
  Gavel,
  XCircle,
  ChevronLeft,
  UserCheck,
  Copy,
  Share2,
  Link,
  ArrowUpRight,
  ArrowDownLeft,
  Download,
} from 'lucide-react';
import {
  useAppSelector,
  useAppDispatch,
  selectCurrentUser,
  selectAuthWallet,
  useLogoutMutation,
  useUpdateProfileMutation,
  useGetPresignedUrlMutation,
  useGetMyAdsQuery,
  useCloseAdMutation,
  useGetMyBidsQuery,
  useWithdrawBidMutation,
  useUpdateRoleMutation,
  useGetTransactionsQuery,
  useGetCreditConfigQuery,
  useGetCreditPackagesQuery,
  selectAccessToken,
  authActions,
} from '@nimbazaar/store';
import { useToast } from '../components/Toast';

type TabType = 'profile' | 'wallet' | 'my-ads' | 'my-bids';

export function Account() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const user = useAppSelector(selectCurrentUser);
  const wallet = useAppSelector(selectAuthWallet);
  const { tab: urlTab } = useParams<{ tab?: string }>();
  const initialTab = (['profile', 'wallet', 'my-ads', 'my-bids'] as TabType[]).includes(urlTab as TabType)
    ? (urlTab as TabType)
    : 'profile';
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  // Sync tab when URL param changes (e.g. navigating from Hero cards)
  useEffect(() => {
    const valid: TabType[] = ['profile', 'wallet', 'my-ads', 'my-bids'];
    if (urlTab && valid.includes(urlTab as TabType)) {
      setActiveTab(urlTab as TabType);
    } else if (!urlTab) {
      setActiveTab('profile');
    }
  }, [urlTab]);

  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();
  const [getPresignedUrl] = useGetPresignedUrlMutation();
  const [updateProfile] = useUpdateProfileMutation();
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Fetch stats for header
  const { data: myAdsData } = useGetMyAdsQuery({ limit: 50 });
  const { data: myBidsData } = useGetMyBidsQuery({ limit: 100 });
  const activeAdsCount = myAdsData?.data?.filter((ad: { status: string }) => ad.status === 'ACTIVE').length ?? 0;
  const activeBids = (myBidsData?.data as { status: string }[] | undefined)?.filter(
    (b) => b.status === 'PENDING' || b.status === 'ACCEPTED'
  ) ?? [];
  const totalBidsCount = activeBids.length;
  const mainFileInputRef = useRef<HTMLInputElement>(null);

  const handleMainAvatarClick = () => {
    mainFileInputRef.current?.click();
  };

  const handleMainAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid File', 'Please select a valid image file (JPEG, PNG, WebP, or GIF)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File Too Large', 'Please select an image smaller than 5MB');
      return;
    }

    setIsUploadingAvatar(true);

    try {
      const { data } = await getPresignedUrl({
        filename: file.name,
        contentType: file.type,
        folder: 'avatars',
        size: file.size,
      }).unwrap();

      const uploadResponse = await fetch(data.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!uploadResponse.ok) throw new Error('Failed to upload image');

      const result = await updateProfile({ avatar: data.publicUrl }).unwrap();

      if (result?.data?.user) {
        dispatch(authActions.setUser(result.data.user));
      } else if (user) {
        dispatch(authActions.setUser({ ...user, avatar: data.publicUrl } as typeof user));
      }

      toast.success('Avatar Updated', 'Your profile picture has been updated');
    } catch (error) {
      const err = error as { data?: { error?: { message?: string } } };
      toast.error('Upload Failed', err?.data?.error?.message || 'Failed to upload image');
    } finally {
      setIsUploadingAvatar(false);
      if (mainFileInputRef.current) mainFileInputRef.current.value = '';
    }
  };

  const handleLogout = async () => {
    try {
      await logout().unwrap();
      dispatch(authActions.logout());
      toast.success('Logged out', 'You have been logged out successfully');
      navigate('/');
    } catch {
      dispatch(authActions.logout());
      navigate('/');
    }
  };

  const menuItems = [
    { id: 'profile' as TabType, label: 'Profile', icon: User },
    { id: 'wallet' as TabType, label: 'Wallet', icon: Wallet },
    { id: 'my-ads' as TabType, label: 'My Ads', icon: FileText },
    { id: 'my-bids' as TabType, label: 'My Bids', icon: Gavel },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Banner - Fixed Gradient Pattern */}
      <div className="relative h-40 sm:h-48 lg:h-56 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900" />
        {/* Decorative Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-96 h-96 bg-secondary-500 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-primary-400 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-secondary-400 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
        </div>
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 -mt-20 sm:-mt-24 lg:-mt-28 pb-8">
        {/* Hidden File Input for Main Avatar */}
        <input
          ref={mainFileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleMainAvatarChange}
          className="hidden"
        />

        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              {/* Avatar */}
              <div className="relative self-center sm:self-auto">
                <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-primary-100 rounded-2xl border-4 border-white shadow-lg flex items-center justify-center overflow-hidden">
                  {user?.avatar ? (
                    <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-12 h-12 text-primary-600" />
                  )}
                  {isUploadingAvatar && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-2xl">
                      <Loader2 className="w-8 h-8 text-white animate-spin" />
                    </div>
                  )}
                </div>
                <button
                  onClick={handleMainAvatarClick}
                  disabled={isUploadingAvatar}
                  className="absolute bottom-1 right-1 w-8 h-8 bg-primary-500 hover:bg-primary-600 rounded-lg flex items-center justify-center text-white shadow-md transition-colors disabled:opacity-50"
                >
                  <Camera className="w-4 h-4" />
                </button>
              </div>

              {/* User Info */}
              <div className="flex-1 text-center sm:text-left">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{user?.name}</h1>
                  {user?.emailVerified && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full self-center sm:self-auto">
                      <CheckCircle className="w-3 h-3" />
                      Verified
                    </span>
                  )}
                </div>
                <p className="text-gray-500 text-sm">{user?.email}</p>
                <p className="text-gray-400 text-xs mt-1">
                  Member since {new Date(user?.createdAt || Date.now()).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </p>
              </div>

              {/* Quick Stats - Desktop */}
              <div className="hidden lg:flex items-center gap-6">
                <div className="text-center px-4">
                  <p className="text-2xl font-bold text-primary-600">{wallet?.balance || 0}</p>
                  <p className="text-xs text-gray-500">Credits</p>
                </div>
                <div className="w-px h-10 bg-gray-200" />
                <div className="text-center px-4">
                  <p className="text-2xl font-bold text-gray-900">{activeAdsCount}</p>
                  <p className="text-xs text-gray-500">Active Ads</p>
                </div>
                <div className="w-px h-10 bg-gray-200" />
                <div className="text-center px-4">
                  <p className="text-2xl font-bold text-gray-900">{totalBidsCount}</p>
                  <p className="text-xs text-gray-500">Total Bids</p>
                </div>
              </div>
            </div>

            {/* Quick Stats - Mobile */}
            <div className="grid grid-cols-3 gap-3 mt-4 lg:hidden">
              <div className="bg-primary-50 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-primary-600">{wallet?.balance || 0}</p>
                <p className="text-xs text-gray-500">Credits</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-gray-900">{activeAdsCount}</p>
                <p className="text-xs text-gray-500">Active Ads</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-gray-900">{totalBidsCount}</p>
                <p className="text-xs text-gray-500">Total Bids</p>
              </div>
            </div>
          </div>

          {/* Tabs - Scrollable on mobile */}
          <div className="border-t border-gray-100">
            <div className="flex overflow-x-auto scrollbar-hide">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); navigate(`/account/${item.id}`, { replace: true }); }}
                  className={`flex items-center gap-2 px-3 sm:px-4 md:px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === item.id
                      ? 'border-primary-500 text-primary-600 bg-primary-50/50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                      {item.badge}
                    </span>
                  )}
                </button>
              ))}
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex items-center gap-2 px-4 sm:px-6 py-4 text-sm font-medium whitespace-nowrap text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors ml-auto"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {activeTab === 'profile' && <ProfileTab user={user} />}
          {activeTab === 'wallet' && <WalletTab wallet={wallet} user={user} />}
          {activeTab === 'my-ads' && <MyAdsTab />}
          {activeTab === 'my-bids' && <MyBidsTab />}
        </div>
      </div>
    </div>
  );
}

// Role Selector Component
function RoleSelector({ currentRole }: { currentRole: string }) {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [updateRole, { isLoading }] = useUpdateRoleMutation();
  const user = useAppSelector(selectCurrentUser);

  const roles = [
    { value: 'BUYER', label: 'Buyer', description: 'Post wanted ads' },
    { value: 'SELLER', label: 'Seller', description: 'Place bids on ads' },
    { value: 'BOTH', label: 'Both', description: 'Post ads & place bids' },
  ];

  const handleRoleChange = async (role: 'BUYER' | 'SELLER' | 'BOTH') => {
    if (role === currentRole) return;
    try {
      const result = await updateRole({ role }).unwrap();
      if (result?.data?.user) {
        dispatch(authActions.setUser(result.data.user));
      } else if (user) {
        dispatch(authActions.setUser({ ...user, role } as typeof user));
      }
      toast.success('Role Updated', `You are now a ${role.charAt(0) + role.slice(1).toLowerCase()}.`);
    } catch {
      toast.error('Error', 'Failed to update role. Please try again.');
    }
  };

  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
        <UserCheck className="w-4 h-4" />
        Account Role
      </label>
      <div className="flex flex-wrap gap-2">
        {roles.map((r) => (
          <button
            key={r.value}
            onClick={() => handleRoleChange(r.value as 'BUYER' | 'SELLER' | 'BOTH')}
            disabled={isLoading}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 ${
              currentRole === r.value
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 text-gray-600 hover:border-primary-300 hover:bg-primary-50'
            }`}
            title={r.description}
          >
            {r.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-1">
        {roles.find((r) => r.value === currentRole)?.description}
      </p>
    </div>
  );
}

// Profile Tab
function ProfileTab({ user }: { user: ReturnType<typeof selectCurrentUser> }) {
  const navigate = useNavigate();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const [showEditModal, setShowEditModal] = useState(false);
  const referralLink = `https://nimbazaar.com/?ref=${user?.id}`;
  const { data: creditConfigData } = useGetCreditConfigQuery();
  const referralBonus = creditConfigData?.data?.REFERRAL_BONUS ?? 50;
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();
  const [getPresignedUrl] = useGetPresignedUrlMutation();
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    location: (user as { location?: string })?.location || '',
    bio: (user as { bio?: string })?.bio || '',
    occupation: (user as { occupation?: string })?.occupation || '',
  });

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid File', 'Please select a valid image file (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File Too Large', 'Please select an image smaller than 5MB');
      return;
    }

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);
    setIsUploadingAvatar(true);

    try {
      // Get presigned URL from API
      const { data } = await getPresignedUrl({
        filename: file.name,
        contentType: file.type,
        folder: 'avatars',
        size: file.size,
      }).unwrap();

      // Upload file directly to S3
      const uploadResponse = await fetch(data.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image to S3');
      }

      // Update profile with new avatar URL
      const result = await updateProfile({
        avatar: data.publicUrl,
      }).unwrap();

      // Update local state
      if (result?.data?.user) {
        dispatch(authActions.setUser(result.data.user));
      } else if (user) {
        dispatch(authActions.setUser({
          ...user,
          avatar: data.publicUrl,
        } as typeof user));
      }

      toast.success('Avatar Updated', 'Your profile picture has been updated');
    } catch (error) {
      console.error('Avatar upload error:', error);
      setAvatarPreview(null);
      const err = error as { data?: { error?: { message?: string } } };
      toast.error('Upload Failed', err?.data?.error?.message || 'Failed to upload image');
    } finally {
      setIsUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleEditSubmit = async () => {
    try {
      const result = await updateProfile({
        name: formData.name,
        phone: formData.phone || undefined,
        location: formData.location || undefined,
        occupation: formData.occupation || undefined,
        bio: formData.bio || undefined,
      }).unwrap();

      // If API doesn't return updated user, manually update state with form data
      if (!result?.data?.user && user) {
        dispatch(authActions.setUser({
          ...user,
          name: formData.name,
          phone: formData.phone || user.phone,
          location: formData.location,
          occupation: formData.occupation,
          bio: formData.bio,
        } as typeof user));
      }

      toast.success('Profile Updated', 'Your profile has been updated successfully');
      setShowEditModal(false);
    } catch (error) {
      const err = error as { data?: { error?: { message?: string } } };
      toast.error('Update Failed', err?.data?.error?.message || 'Failed to update profile');
    }
  };

  // Get current avatar (preview or actual)
  const currentAvatar = avatarPreview || user?.avatar;

  const getProfileCompletion = () => {
    let completed = 0;
    if (user?.name) completed++;
    if (user?.email) completed++;
    if (user?.emailVerified) completed++;
    if (user?.phone) completed++;
    if ((user as { location?: string })?.location) completed++;
    if ((user as { occupation?: string })?.occupation) completed++;
    if ((user as { bio?: string })?.bio) completed++;
    if (user?.avatar) completed++;
    return Math.round((completed / 8) * 100);
  };

  const profileCompletion = getProfileCompletion();

  return (
    <>
      {/* Edit Profile Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowEditModal(false)}
          />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-4xl max-h-[90vh] sm:max-h-[85vh] flex flex-col safe-bottom">
            {/* Modal Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Edit Profile</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 min-h-0 px-4 sm:px-6 py-4 overflow-y-auto">
              {/* Hidden File Input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleAvatarChange}
                className="hidden"
              />

              {/* Profile Picture - Inline */}
              <div className="flex items-center gap-4 mb-5">
                <div className="relative">
                  <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center overflow-hidden">
                    {currentAvatar ? (
                      <img src={currentAvatar} alt={user?.name} className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-primary-600" />
                    )}
                    {isUploadingAvatar && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-xl">
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleAvatarClick}
                    disabled={isUploadingAvatar}
                    className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary-500 hover:bg-primary-600 rounded-md flex items-center justify-center text-white shadow-md transition-colors disabled:opacity-50"
                  >
                    <Camera className="w-3 h-3" />
                  </button>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{user?.name}</p>
                  <button
                    onClick={handleAvatarClick}
                    disabled={isUploadingAvatar}
                    className="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                  >
                    {isUploadingAvatar ? 'Uploading...' : 'Change your profile photo'}
                  </button>
                </div>
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Full Name */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                    <User className="w-3.5 h-3.5" />
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email (Read-only) */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                    <Mail className="w-3.5 h-3.5" />
                    Email
                    {user?.emailVerified && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                        <CheckCircle className="w-2.5 h-2.5" />
                        Verified
                      </span>
                    )}
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed text-sm"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                    <Phone className="w-3.5 h-3.5" />
                    Phone
                    {!user?.phone && <span className="text-xs text-red-500">Not provided</span>}
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="Enter phone number"
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    Location
                    {!(user as { location?: string })?.location && <span className="text-xs text-red-500">Not provided</span>}
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="Enter city or location"
                  />
                </div>

                {/* Occupation */}
                <div className="sm:col-span-2">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                    <Briefcase className="w-3.5 h-3.5" />
                    Occupation
                    {!(user as { occupation?: string })?.occupation && <span className="text-xs text-red-500">Not provided</span>}
                  </label>
                  <input
                    type="text"
                    value={formData.occupation}
                    onChange={(e) => setFormData({ ...formData, occupation: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="What do you do?"
                  />
                </div>

                {/* Bio */}
                <div className="sm:col-span-2">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                    <Edit3 className="w-3.5 h-3.5" />
                    Bio
                    {!(user as { bio?: string })?.bio && <span className="text-xs text-red-500">Not provided</span>}
                  </label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about yourself..."
                  />
                </div>
              </div>

              {/* Profile Completion - Compact */}
              <div className="mt-4 bg-gray-50 rounded-lg p-3 flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">Profile Completion</span>
                    <span className="font-semibold text-primary-600">{profileCompletion}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-primary-500 rounded-full transition-all" style={{ width: `${profileCompletion}%` }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex-shrink-0 flex flex-col-reverse sm:flex-row items-stretch sm:items-center sm:justify-end gap-2 sm:gap-3 px-4 sm:px-6 py-4 sm:py-5 border-t border-gray-100 bg-gray-50 sm:rounded-b-2xl">
              <button
                onClick={() => setShowEditModal(false)}
                disabled={isUpdating}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={isUpdating}
                className="flex items-center justify-center gap-2 px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? (
                  <>
                    <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Info */}
      <div className="lg:col-span-2 space-y-6">
        {/* Personal Information */}
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
            <button
              onClick={() => setShowEditModal(true)}
              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              <Edit3 className="w-4 h-4" />
              <span className="hidden sm:inline">Edit</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <User className="w-4 h-4" />
                Full Name
              </label>
              <p className="text-gray-900 font-medium">{user?.name || '-'}</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <Mail className="w-4 h-4" />
                Email Address
              </label>
              <div className="flex items-center gap-2">
                <p className="text-gray-900 font-medium">{user?.email || '-'}</p>
                {user?.emailVerified && (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                )}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <Phone className="w-4 h-4" />
                Phone Number
              </label>
              <p className="text-gray-900 font-medium">{user?.phone || 'Not provided'}</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <MapPin className="w-4 h-4" />
                Location
              </label>
              <p className="text-gray-900 font-medium">{(user as { location?: string })?.location || 'Not provided'}</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <Briefcase className="w-4 h-4" />
                Occupation
              </label>
              <p className="text-gray-900 font-medium">{(user as { occupation?: string })?.occupation || 'Not provided'}</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <Calendar className="w-4 h-4" />
                Member Since
              </label>
              <p className="text-gray-900 font-medium">
                {new Date(user?.createdAt || Date.now()).toLocaleDateString('en-US', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>

            <div>
              <RoleSelector currentRole={(user as { role?: string })?.role || 'BUYER'} />
            </div>

            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <Edit3 className="w-4 h-4" />
                Bio
              </label>
              <p className="text-gray-900 font-medium">{(user as { bio?: string })?.bio || 'Not provided'}</p>
            </div>
          </div>
        </div>

      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        {/* Referral */}
        <div className="bg-gradient-to-br from-green-500 to-green-700 rounded-2xl p-4 sm:p-6 text-white">
          <Gift className="w-8 h-8 mb-3" />
          <h3 className="font-semibold mb-1">Refer & Earn</h3>
          <p className="text-sm text-green-100 mb-3">Get {referralBonus} free credits for each friend you refer!</p>
          <div className="bg-white/20 rounded-lg px-3 py-2 mb-3 flex items-center gap-2">
            <span className="text-xs text-green-100 truncate flex-1">{referralLink}</span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(referralLink);
                toast.success('Copied!', 'Referral link copied to clipboard.');
              }}
              className="text-xs bg-white/30 hover:bg-white/40 px-2 py-1 rounded font-medium transition-colors shrink-0"
            >
              Copy
            </button>
          </div>
          <button
            onClick={() => {
              if (navigator.share) {
                navigator.share({ title: 'Join NimBazaar', text: `Join NimBazaar and get ${referralBonus} free credits!`, url: referralLink });
              } else {
                navigator.clipboard.writeText(referralLink);
                toast.success('Link Copied!', `Share it with friends to earn ${referralBonus} credits each.`);
              }
            }}
            className="w-full py-2 bg-white text-green-600 font-medium rounded-lg hover:bg-green-50 transition-colors"
          >
            Invite Friends
          </button>
        </div>

        {/* Account Completion */}
        <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl shadow-sm p-4 sm:p-6 text-white">
          <h2 className="font-semibold mb-2">Complete Your Profile</h2>
          <p className="text-sm text-primary-100 mb-4">Add more details to increase trust</p>
          <div className="mb-3">
            <div className="flex justify-between text-sm mb-1">
              <span>Profile Completion</span>
              <span>{profileCompletion}%</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-secondary-400 rounded-full transition-all" style={{ width: `${profileCompletion}%` }} />
            </div>
          </div>
          <button
            onClick={() => setShowEditModal(true)}
            className="w-full py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
          >
            Complete Now
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

// Transaction type display config
const TRANSACTION_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: 'credit' | 'debit' }> = {
  SIGNUP_BONUS: { label: 'Signup Bonus', color: 'text-green-600', bgColor: 'bg-green-100', icon: 'credit' },
  REFERRAL_BONUS: { label: 'Referral Bonus', color: 'text-green-600', bgColor: 'bg-green-100', icon: 'credit' },
  AD_CREATION: { label: 'Ad Created', color: 'text-red-600', bgColor: 'bg-red-100', icon: 'debit' },
  AD_REWARD: { label: 'Ad Reward', color: 'text-green-600', bgColor: 'bg-green-100', icon: 'credit' },
  CREDIT_PURCHASE: { label: 'Credits Purchased', color: 'text-green-600', bgColor: 'bg-green-100', icon: 'credit' },
  REFUND: { label: 'Refund', color: 'text-green-600', bgColor: 'bg-green-100', icon: 'credit' },
  ADMIN_ADJUSTMENT: { label: 'Admin Adjustment', color: 'text-blue-600', bgColor: 'bg-blue-100', icon: 'credit' },
};

// Wallet Tab
function WalletTab({ wallet, user }: { wallet: ReturnType<typeof selectAuthWallet>; user: ReturnType<typeof selectCurrentUser> }) {
  const toast = useToast();
  const token = useAppSelector(selectAccessToken);
  const [txPage, setTxPage] = useState(1);
  const { data: txData, isLoading: txLoading } = useGetTransactionsQuery(
    { page: txPage, limit: 4 },
    { refetchOnFocus: true, refetchOnMountOrArgChange: true }
  );
  const { data: packagesData } = useGetCreditPackagesQuery();
  const creditPackages = packagesData?.data ?? [];
  const [showReport, setShowReport] = useState(false);

  // Fetch all transactions for this month (max 50 per API)
  const { data: monthTxData } = useGetTransactionsQuery(
    { page: 1, limit: 50 },
    { refetchOnFocus: true, refetchOnMountOrArgChange: true }
  );

  const transactions = txData?.data ?? [];
  const txMeta = txData?.meta;
  const allMonthTx = monthTxData?.data ?? [];

  // Calculate this month's spending from all fetched transactions
  const now = new Date();

  const monthTransactions = allMonthTx.filter((t) => {
    const txDate = new Date(t.createdAt);
    return txDate.getFullYear() === now.getFullYear() && txDate.getMonth() === now.getMonth();
  });

  const monthSpent = monthTransactions
    .filter((t) => t.amount < 0)
    .reduce((sum, t) => sum + Math.abs(t.amount), 0);
  const monthEarned = monthTransactions.filter((t) => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
  const monthSpentTotal = monthSpent;

  const [downloading, setDownloading] = useState(false);

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const apiUrl = import.meta.env?.VITE_API_URL || '/api';
      const res = await fetch(
        `${apiUrl}/wallet/report?year=${now.getFullYear()}&month=${now.getMonth() + 1}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) throw new Error('Failed to download');

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nimbazaar-report-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Report Downloaded', 'Your transaction report has been saved.');
    } catch {
      toast.error('Download Failed', 'Failed to download report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Content */}
      <div className="lg:col-span-2 space-y-6">
        {/* Available Balance */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
              <Coins className="w-5 h-5" />
            </div>
            <div>
              <p className="text-primary-200 text-xs">Available Balance</p>
              <p className="text-2xl font-bold leading-none">{wallet?.balance || 0} <span className="text-sm font-normal text-primary-200">credits</span></p>
            </div>
          </div>
          <button disabled className="px-5 py-2.5 bg-white/15 rounded-xl text-sm font-medium flex items-center gap-2 opacity-60 cursor-not-allowed">
            <Plus className="w-4 h-4" />
            Add Credits
            <span className="text-xs opacity-75">(Coming Soon)</span>
          </button>
        </div>
        {/* Transactions */}
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Transaction History</h2>
            {txMeta && txMeta.total > 0 && (
              <span className="text-sm text-gray-500">{txMeta.total} total</span>
            )}
          </div>

          {txLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-3 animate-pulse">
                  <div className="w-10 h-10 bg-gray-200 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-16" />
                </div>
              ))}
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-900 font-medium mb-1">No transactions yet</p>
              <p className="text-gray-500 text-sm">Your transaction history will appear here</p>
            </div>
          ) : (
            <>
              <div className="space-y-1">
                {transactions.map((tx) => {
                  const config = TRANSACTION_CONFIG[tx.type] || { label: tx.type, color: 'text-gray-600', bgColor: 'bg-gray-100', icon: 'credit' as const };
                  const isCredit = tx.amount > 0;
                  return (
                    <div key={tx.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                      <div className={`w-10 h-10 ${config.bgColor} rounded-xl flex items-center justify-center shrink-0`}>
                        {isCredit ? (
                          <ArrowUpRight className={`w-5 h-5 ${config.color}`} />
                        ) : (
                          <ArrowDownLeft className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{config.label}</p>
                        <p className="text-xs text-gray-500 truncate">{tx.description || config.label}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`text-sm font-semibold ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                          {isCredit ? '+' : ''}{tx.amount}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(tx.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {txMeta && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setTxPage((p) => Math.max(1, p - 1))}
                    disabled={!txMeta.hasPrevPage}
                    className="flex items-center gap-1 px-2 sm:px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed min-h-[40px]"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <span className="text-sm text-gray-500">
                    Page {txMeta.page} of {txMeta.totalPages}
                  </span>
                  <button
                    onClick={() => setTxPage((p) => p + 1)}
                    disabled={!txMeta.hasNextPage}
                    className="flex items-center gap-1 px-2 sm:px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed min-h-[40px]"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        {/* Credit Packages */}
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Buy Credits</h2>
          <div className="space-y-3">
            {creditPackages.map((pkg) => (
              <button
                key={pkg.credits}
                className={`w-full p-4 rounded-xl border-2 transition-all ${
                  pkg.popular
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-primary-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      pkg.popular ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <Coins className="w-5 h-5" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">{pkg.credits} Credits</p>
                      <p className="text-sm text-gray-500">Rs. {pkg.price}</p>
                    </div>
                  </div>
                  {pkg.popular && (
                    <span className="px-2 py-1 bg-primary-500 text-white text-xs font-medium rounded-full">
                      Popular
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* This Month Spent */}
        <div className="bg-gradient-to-br from-secondary-500 to-secondary-700 rounded-2xl p-5 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <p className="text-secondary-100 text-xs">This Month Spent</p>
              <p className="text-2xl font-bold leading-none">{monthSpent}</p>
            </div>
          </div>
          <button
            onClick={() => setShowReport(true)}
            className="w-full py-2.5 bg-white/20 hover:bg-white/30 rounded-xl text-sm font-medium transition-colors"
          >
            View Report
          </button>
        </div>

      </div>

      {/* Report Modal */}
      {showReport && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 sm:p-6">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-800 p-5 text-white flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-lg font-bold">Monthly Report</h2>
                <p className="text-primary-200 text-sm">{now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={downloadReport}
                  disabled={downloading}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Download Excel
                </button>
                <button
                  onClick={() => setShowReport(false)}
                  className="w-9 h-9 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-3 p-5 border-b border-gray-100 shrink-0">
              <div className="bg-green-50 rounded-xl p-3 text-center">
                <p className="text-xs text-green-600 mb-1">Earned</p>
                <p className="text-xl font-bold text-green-700">+{monthEarned}</p>
              </div>
              <div className="bg-red-50 rounded-xl p-3 text-center">
                <p className="text-xs text-red-600 mb-1">Spent</p>
                <p className="text-xl font-bold text-red-700">-{monthSpentTotal}</p>
              </div>
              <div className="bg-primary-50 rounded-xl p-3 text-center">
                <p className="text-xs text-primary-600 mb-1">Net</p>
                <p className={`text-xl font-bold ${monthEarned - monthSpentTotal >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {monthEarned - monthSpentTotal >= 0 ? '+' : ''}{monthEarned - monthSpentTotal}
                </p>
              </div>
            </div>

            {/* Transaction List */}
            <div className="flex-1 overflow-y-auto p-5 max-h-[400px]">
              <p className="text-sm font-medium text-gray-500 mb-3">{monthTransactions.length} transactions this month</p>
              {monthTransactions.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No transactions this month</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {monthTransactions.map((tx) => {
                    const config = TRANSACTION_CONFIG[tx.type] || { label: tx.type, color: 'text-gray-600', bgColor: 'bg-gray-100' };
                    const isCredit = tx.amount > 0;
                    return (
                      <div key={tx.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                        <div className={`w-9 h-9 ${config.bgColor} rounded-lg flex items-center justify-center shrink-0`}>
                          {isCredit ? (
                            <ArrowUpRight className={`w-4 h-4 ${config.color}`} />
                          ) : (
                            <ArrowDownLeft className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{config.label}</p>
                          <p className="text-xs text-gray-500 truncate">{tx.description || config.label}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className={`text-sm font-semibold ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                            {isCredit ? '+' : ''}{tx.amount}
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(tx.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// My Ads Tab
function MyAdsTab() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading } = useGetMyAdsQuery();
  const [closeAd, { isLoading: isClosing }] = useCloseAdMutation();
  const [closingAdId, setClosingAdId] = useState<string | null>(null);

  const ads = data?.data ?? [];

  const activeCount = ads.filter((a) => a.status === 'ACTIVE').length;
  const closedCount = ads.filter((a) => a.status === 'CLOSED').length;
  const expiredCount = ads.filter((a) => a.status === 'EXPIRED').length;
  const totalBids = ads.reduce((sum, a) => sum + (a.bidCount || 0), 0);

  const handleClose = async (adId: string) => {
    setClosingAdId(adId);
    try {
      await closeAd(adId).unwrap();
      toast.success('Ad Closed', 'Your ad has been closed successfully.');
    } catch (error) {
      const err = error as { data?: { error?: { message?: string } } };
      toast.error('Error', err?.data?.error?.message || 'Failed to close ad.');
    } finally {
      setClosingAdId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full"><CheckCircle className="w-3 h-3" />Active</span>;
      case 'CLOSED':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">Closed</span>;
      case 'EXPIRED':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-full"><Clock className="w-3 h-3" />Expired</span>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">My Ads</h2>
          <p className="text-gray-500 text-sm">Manage your active and past ads</p>
        </div>
        <button
          onClick={() => navigate('/post-ad')}
          className="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create New Ad
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        {[
          { label: 'Active', value: activeCount, color: 'bg-green-500' },
          { label: 'Expired', value: expiredCount, color: 'bg-yellow-500' },
          { label: 'Closed', value: closedCount, color: 'bg-gray-500' },
          { label: 'Total Bids', value: totalBids, color: 'bg-primary-500' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-2 h-2 rounded-full ${stat.color}`} />
              <span className="text-sm text-gray-500">{stat.label}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin mx-auto" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && ads.length === 0 && (
        <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-12 text-center">
          <div className="w-20 h-20 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Megaphone className="w-10 h-10 text-primary-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No ads yet</h3>
          <p className="text-gray-500 mb-6 max-w-sm mx-auto">
            Create your first ad to start receiving bids from potential buyers
          </p>
          <button
            onClick={() => navigate('/post-ad')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Create Your First Ad
          </button>
        </div>
      )}

      {/* Ads List */}
      {!isLoading && ads.length > 0 && (
        <div className="space-y-3">
          {ads.map((ad) => (
            <div key={ad.id} className="bg-white rounded-xl shadow-sm p-4 flex gap-3 sm:gap-4">
              {/* Thumbnail */}
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-lg overflow-hidden shrink-0">
                {ad.images?.[0] ? (
                  <img src={ad.images[0].url} alt={ad.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Megaphone className="w-6 h-6 text-gray-300" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <button
                      onClick={() => navigate(`/ad/${ad.slug}`)}
                      className="text-sm font-semibold text-gray-900 hover:text-primary-600 truncate block max-w-full text-left"
                    >
                      {ad.title}
                    </button>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {getStatusBadge(ad.status)}
                      <span className="text-xs text-gray-500">{ad.category.name}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    {ad.viewCount}
                  </span>
                  <span className="flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {ad.bidCount} bids
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(ad.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              </div>

              {/* Actions */}
              {ad.status === 'ACTIVE' && (
                <div className="self-center flex flex-col gap-1.5 shrink-0">
                  <button
                    onClick={() => navigate(`/edit-ad/${ad.id}`)}
                    className="px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleClose(ad.id)}
                    disabled={isClosing && closingAdId === ad.id}
                    className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isClosing && closingAdId === ad.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      'Close'
                    )}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// My Bids Tab
function MyBidsTab() {
  const navigate = useNavigate();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const limit = 10;

  const { data, isLoading } = useGetMyBidsQuery({ page, limit, status: statusFilter || undefined });
  const [withdrawBid, { isLoading: isWithdrawing }] = useWithdrawBidMutation();
  const [withdrawingBidId, setWithdrawingBidId] = useState<string | null>(null);

  const bids = (data?.data ?? []) as Array<{
    id: string;
    price: number;
    description: string;
    status: string;
    createdAt: string;
    ad: {
      id: string;
      title: string;
      slug: string;
      images?: { url: string }[];
      category?: { name: string };
      user?: { name: string };
    };
  }>;
  const meta = data?.meta;

  const handleWithdraw = async (bidId: string) => {
    setWithdrawingBidId(bidId);
    try {
      await withdrawBid(bidId).unwrap();
      toast.success('Bid Withdrawn', 'Your bid has been withdrawn.');
    } catch {
      toast.error('Error', 'Failed to withdraw bid. Please try again.');
    } finally {
      setWithdrawingBidId(null);
    }
  };

  const statusFilters = [
    { value: '', label: 'All' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'ACCEPTED', label: 'Accepted' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'WITHDRAWN', label: 'Withdrawn' },
  ];

  const getBidStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full"><Clock className="w-3 h-3" />Pending</span>;
      case 'ACCEPTED':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full"><CheckCircle className="w-3 h-3" />Accepted</span>;
      case 'REJECTED':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-full"><XCircle className="w-3 h-3" />Rejected</span>;
      case 'WITHDRAWN':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">Withdrawn</span>;
      default:
        return null;
    }
  };

  const formatPrice = (price: number): string => {
    if (price >= 10000000) {
      return `Rs. ${(price / 10000000).toFixed(price % 10000000 === 0 ? 0 : 1)} Crore`;
    } else if (price >= 100000) {
      return `Rs. ${(price / 100000).toFixed(price % 100000 === 0 ? 0 : 1)} Lac`;
    } else if (price >= 1000) {
      return `Rs. ${(price / 1000).toFixed(price % 1000 === 0 ? 0 : 1)} Thousand`;
    }
    return `Rs. ${price.toLocaleString()}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">My Bids</h2>
          <p className="text-gray-500 text-sm">Track all your placed bids</p>
        </div>
      </div>

      {/* Status Filter Pills */}
      <div className="flex flex-wrap gap-2">
        {statusFilters.map((f) => (
          <button
            key={f.value}
            onClick={() => { setStatusFilter(f.value); setPage(1); }}
            className={`px-3 sm:px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
              statusFilter === f.value
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin mx-auto" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && bids.length === 0 && (
        <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-12 text-center">
          <div className="w-20 h-20 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Gavel className="w-10 h-10 text-primary-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {statusFilter ? 'No bids found' : 'No bids yet'}
          </h3>
          <p className="text-gray-500 mb-6 max-w-sm mx-auto">
            {statusFilter
              ? 'Try changing the filter to see more bids.'
              : 'Browse ads and place bids to see them here.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 transition-colors"
          >
            Browse Ads
          </button>
        </div>
      )}

      {/* Bids List */}
      {!isLoading && bids.length > 0 && (
        <div className="space-y-3">
          {bids.map((bid) => (
            <div key={bid.id} className="bg-white rounded-xl shadow-sm p-4 flex gap-3 sm:gap-4">
              {/* Thumbnail */}
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-lg overflow-hidden shrink-0">
                {bid.ad?.images?.[0] ? (
                  <img src={bid.ad.images[0].url} alt={bid.ad.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Megaphone className="w-6 h-6 text-gray-300" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <button
                      onClick={() => navigate(`/ad/${bid.ad.slug}`)}
                      className="text-sm font-semibold text-gray-900 hover:text-primary-600 truncate block max-w-full text-left"
                    >
                      {bid.ad.title}
                    </button>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {getBidStatusBadge(bid.status)}
                      {bid.ad.category && (
                        <span className="text-xs text-gray-500">{bid.ad.category.name}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-secondary-500 font-bold text-sm">
                      {formatPrice(bid.price)}
                    </div>
                  </div>
                </div>

                {bid.description && (
                  <p className="text-gray-500 text-xs mt-1.5 line-clamp-1">{bid.description}</p>
                )}

                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(bid.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  {bid.ad.user && (
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {bid.ad.user.name}
                    </span>
                  )}
                </div>
              </div>

              {/* Withdraw Action */}
              {bid.status === 'PENDING' && (
                <button
                  onClick={() => handleWithdraw(bid.id)}
                  disabled={isWithdrawing && withdrawingBidId === bid.id}
                  className="self-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 shrink-0"
                >
                  {isWithdrawing && withdrawingBidId === bid.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    'Withdraw'
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={!meta.hasPrevPage}
            className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {meta.page} of {meta.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!meta.hasNextPage}
            className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

